/**
 * Station Mode (`/station`) — the tablet-first, chromeless shop-floor kiosk.
 *
 * A parallel surface to the supervisor Floor (`/production/floor`), built for a
 * non-technical operator who lives in this one screen all shift: a calm, light,
 * premium surface — a quiet header with a live pending readout, "what's waiting
 * here" as clean cards, and a tap that opens the size → count → submit sheet.
 * Deliberately outside the `(workspace)` layout so there's no sidebar/topbar to
 * wander off into — the whole screen is the job.
 *
 * Station-scoped (not per-worker): the queue is this station's, no login step.
 * Reuses `wip.queue` / `wip.forStyle` / `wip.record` — no backend change.
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { RefreshCw, Repeat, LogOut } from "lucide-react";
import type { wip as wipContract } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.js";
import { stationByKey } from "../../../lib/garment-stations.js";
import { useStation } from "../lib/use-station.js";
import { StrikeApprovalsBanner } from "../../../components/garment/strike-approvals-banner.js";
import { StationPicker } from "./station-picker.js";
import { StationQueue, type QueueRow } from "./station-queue.js";
import { RecordSheet } from "./record-sheet.js";

export function StationModePage() {
  const me = trpc.identity.me.useQuery({});
  const company = me.data?.company ?? null;
  const companyId = company?.id ?? null;
  const garmentEntitled = (company?.modules ?? []).some((m) => m.startsWith("garment"));

  const { station, setStation, clearStation } = useStation(companyId);
  const [active, setActive] = useState<QueueRow | null>(null);

  if (me.isLoading) {
    return <p className="p-8 text-center text-muted">Loading…</p>;
  }

  if (!garmentEntitled) {
    return (
      <div className="fixed inset-0 grid place-items-center bg-bg px-6 text-center">
        <div>
          <div className="text-lg font-semibold text-ink">Station Mode isn&rsquo;t available here</div>
          <p className="mt-1 text-sm text-muted">This workspace doesn&rsquo;t have the Garment add-on enabled.</p>
        </div>
      </div>
    );
  }

  if (!station) {
    return <StationPicker onPick={setStation} />;
  }

  return (
    <div className="fixed inset-0 overflow-y-auto bg-bg">
      <StationHeader
        station={station}
        tradeName={company?.tradeName ?? "Workspace"}
        onChange={clearStation}
      />
      <main className="mx-auto w-full max-w-[720px] pt-4">
        <StrikeApprovalsBanner className="mx-4 mb-2" />
        <StationQueue stage={station} onSelect={setActive} />
      </main>

      {active ? (
        <RecordSheet
          styleId={active.styleId}
          styleRef={active.styleRef}
          name={active.name}
          stage={station}
          onClose={() => setActive(null)}
        />
      ) : null}
    </div>
  );
}

/** Quiet, light header with a live pending readout. Reuses the cached `wip.queue`. */
function StationHeader({
  station,
  tradeName,
  onChange,
}: {
  station: wipContract.WipStage;
  tradeName: string;
  onChange: () => void;
}) {
  const utils = trpc.useUtils();
  const q = trpc.wip.queue.useQuery({ stage: station });
  const meta = stationByKey(station);

  const rows = q.data?.rows ?? [];
  const pendingOrders = rows.filter((r) => r.balance > 0).length;
  const pendingPieces = rows.reduce((sum, r) => sum + Math.max(0, r.balance), 0);

  return (
    <header className="sticky top-0 z-10 border-b border-line bg-paper/90 backdrop-blur">
      <div className="mx-auto flex w-full max-w-[720px] items-center gap-3 px-4 py-3">
        {meta ? (
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-sky-50 text-sky-700">
            <meta.icon className="h-5 w-5" />
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="truncate text-base font-semibold text-ink">{meta?.label ?? "Station"}</div>
          <div className="truncate text-xs text-muted">
            {tradeName}
            {pendingOrders > 0
              ? ` · ${pendingPieces.toLocaleString("en-IN")} pcs across ${pendingOrders} order${pendingOrders > 1 ? "s" : ""}`
              : " · all clear"}
          </div>
        </div>

        <button
          type="button"
          onClick={() => utils.wip.invalidate()}
          aria-label="Refresh queue"
          className="grid h-10 w-10 shrink-0 place-items-center rounded-lg text-muted transition hover:bg-raised hover:text-ink"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onChange}
          className="flex h-10 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium text-muted transition hover:bg-raised hover:text-ink"
        >
          <Repeat className="h-4 w-4" /> Change
        </button>
        <Link
          to="/"
          aria-label="Exit to the app"
          className="grid h-10 w-10 shrink-0 place-items-center rounded-lg text-muted transition hover:bg-raised hover:text-ink"
        >
          <LogOut className="h-4 w-4" />
        </Link>
      </div>
    </header>
  );
}
